import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import { triggerRefresh } from "@/hooks/useRefresh";
import { WASTAGE_REASONS } from "@/lib/format";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const TYPES = [
  { v: "receive", l: "Receive (stock in)" },
  { v: "consume", l: "Consume / Issue (stock out)" },
  { v: "adjust", l: "Adjust (+/- correction)" },
  { v: "wastage", l: "Wastage" },
];

export function MovementDialog({ open, onOpenChange, defaultType = "receive", lockType = false }) {
  const { writableBranches, selected } = useBranch();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ type: defaultType, branch_id: "", item_id: "", qty: "", reason: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/items").then((r) => setItems(r.data));
    const branchDefault = writableBranches.some((b) => b.branch_id === selected) ? selected : writableBranches[0]?.branch_id || "";
    setForm({ type: defaultType, branch_id: branchDefault, item_id: "", qty: "", reason: defaultType === "wastage" ? "spoilage" : "" });
  }, [open, defaultType, selected, writableBranches]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/movements", { ...form, qty: parseFloat(form.qty) });
      toast.success(form.type === "wastage" ? "Wastage logged" : "Movement recorded");
      onOpenChange(false);
      triggerRefresh();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const isWastage = form.type === "wastage";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="movement-dialog">
        <DialogHeader>
          <DialogTitle>{isWastage ? "Log wastage" : "Record stock movement"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {!lockType && (
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, reason: v === "wastage" ? "spoilage" : "" })}>
                <SelectTrigger data-testid="movement-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <Label>Branch</Label>
            <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
              <SelectTrigger data-testid="movement-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
              <SelectContent>{writableBranches.map((b) => <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Item</Label>
            <Select value={form.item_id} onValueChange={(v) => setForm({ ...form, item_id: v })}>
              <SelectTrigger data-testid="movement-item-select"><SelectValue placeholder="Select item" /></SelectTrigger>
              <SelectContent className="max-h-64">
                {items.map((i) => <SelectItem key={i.item_id} value={i.item_id}>{i.name} ({i.unit})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Quantity {form.type === "adjust" && "(use negative to deduct)"}</Label>
            <Input type="number" step="any" data-testid="movement-qty-input" value={form.qty}
              onChange={(e) => setForm({ ...form, qty: e.target.value })} required />
          </div>
          <div className="space-y-2">
            <Label>Reason</Label>
            {isWastage ? (
              <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                <SelectTrigger data-testid="wastage-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>{WASTAGE_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <Input data-testid="movement-reason-input" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Optional note" />
            )}
          </div>
          <Button type="submit" className="w-full" disabled={loading || !form.item_id || !form.branch_id} data-testid="movement-submit-btn">
            {loading ? "Saving…" : "Save"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
